/**
 * Self-documenting reports folder.
 *
 * The output directory accumulates artifacts whose taxonomy is obvious to us
 * and opaque to a newcomer (raw-run.json vs story-report vs scenario index vs
 * rendered reports). Drop a README into it ONCE, at the point of contact, so
 * the folder explains itself. Never overwrites: if a README.md exists (ours or
 * the user's), it is left alone.
 */
import * as fs from "node:fs";
import * as path from "node:path";

const README = `# executable-stories artifacts

Generated by your test reporter and the \`executable-stories\` CLI. Safe to
gitignore; every file regenerates from a test run.

| File | What it is | Who reads it |
| ---- | ---------- | ------------ |
| \`raw-run.json\` | The run record your test reporter writes — the single input every consumer below reads | \`executable-stories\` CLI, MCP server, docs site |
| \`*.story-report.json\` | StoryReport v1, the canonical machine contract | Agents, custom tooling |
| \`*.scenario-index.json\` | One-line-per-scenario index (with content hashes) for discovery and failure triage | Agents, \`check-explainers\` frontmatter |
| \`*.html\` | Single-file human report for this run | CI attachments, quick local viewing |
| \`*.md\` | Markdown report | PRs, wikis |
| \`*.junit.xml\`, \`*.cucumber.json\`, \`*.ndjson\` | Interop formats | CI dashboards, Cucumber tooling |
| \`by-file/\` | One report per test source file, named after it | The CLI and the docs site, as the input to any combined view |

Each test file owns a report in \`by-file/\`. Running one test file rewrites one
report and leaves the rest alone, which is why a focused run does not shrink the
docs to the file it ran.

Any combined view is derived from that directory rather than accumulated:

\`\`\`bash
executable-stories format reports/by-file --format html --output-dir reports
\`\`\`

Delete a report and its scenarios leave the combined view; delete the directory
and the next full test run rebuilds it. \`executable-stories runs status\` shows
how old each file's results are.

Useful commands (all take \`raw-run.json\`):

- \`executable-stories check <raw-run.json>\` — failure summary with non-zero exit (agent backpressure)
- \`executable-stories list <raw-run.json>\` — scenario listing
- \`executable-stories compare <baseline> <current>\` — behavioural diff between runs
- \`executable-stories dev\` — live docs site (scaffold once with \`init-astro --install\`)

<!-- generated by executable-stories; edit or delete freely — it will not be rewritten -->
`;

/**
 * Write the artifacts README into the output dir unless one already exists.
 * Returns true only when this call created it — the CLI uses that as its
 * "first contact with this output dir" signal for one-time hints.
 */
export function writeArtifactsReadme(outputDir: string): boolean {
  try {
    const target = path.join(outputDir, "README.md");
    if (fs.existsSync(target)) return false;
    fs.mkdirSync(outputDir, { recursive: true });
    fs.writeFileSync(target, README, "utf8");
    return true;
  } catch {
    // A missing README must never fail a report run.
    return false;
  }
}
